/**
 * ChallengeProgressPanel — the caller's own progress, the cooperative team
 * total, and the completion / achievement state.
 *
 * Presentational. `unknown` activity (results that were not measured) is shown
 * as its own line: it is never counted as a fail or a zero. Team totals are
 * shown only when the server releases them (three or more participants). A
 * completion is recognised by the platform's achievements (one badge per
 * challenge); this panel only displays what the server reports.
 */

import type { ChallengeDetail } from './challengeTypes';
import { conditionUnit, TEAM_TOTAL_MIN_PARTICIPANTS } from './challengeTypes';

const pctOf = (value: number, target: number | null) => (target ? Math.min(100, Math.round((value / target) * 100)) : 0);

export function ChallengeAchievementNotice({ challenge }: { challenge: ChallengeDetail }) {
  const a = challenge.me.achievement;
  if (!a) return null;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="challenge-achievement">
      <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-400 text-sm font-bold text-white">
        ★
      </span>
      <div>
        <p className="font-medium text-amber-900">Achievement earned: Team Challenge Completed</p>
        <p className="text-xs text-amber-800">
          Earned {new Date(a.earnedAt).toLocaleDateString()}. It appears with your other badges; it shows on your public
          profile only if you choose to show badges there.
        </p>
      </div>
    </div>
  );
}

export function ChallengeProgressPanel({ challenge }: { challenge: ChallengeDetail }) {
  const c = challenge.condition;
  const unit = c ? conditionUnit(c) : '';
  const target = c?.target ?? null;
  const mine = challenge.me.progress;
  const cooperative = challenge.goalScope === 'team';
  const agg = challenge.aggregate;

  return (
    <div className="space-y-4">
      <ChallengeAchievementNotice challenge={challenge} />

      {challenge.me.joined && mine && (
        <div data-testid="challenge-my-progress">
          <div className="flex justify-between text-sm">
            <span className="font-medium">{cooperative ? 'Your contribution' : 'Your progress'}</span>
            <span>
              {mine.value}
              {!cooperative && target !== null ? ` / ${target}` : ''} {unit}
            </span>
          </div>
          {!cooperative && (
            <div className="mt-1 h-2 rounded bg-gray-100">
              <div className="h-2 rounded bg-primary-600" style={{ width: `${pctOf(mine.value, target)}%` }} />
            </div>
          )}
          {mine.unknown > 0 && (
            <p className="mt-1 text-xs text-gray-500" data-testid="challenge-unknown">
              {mine.unknown} {mine.unknown === 1 ? 'session has' : 'sessions have'} no measured result yet. They are not
              counted, and they do not count against you.
            </p>
          )}
          {!cooperative && mine.completed && (
            <p className="mt-1 text-sm text-green-700" data-testid="challenge-completed">
              Goal reached.
            </p>
          )}
        </div>
      )}

      {cooperative && (
        <div data-testid="challenge-team-total">
          {agg.withheld ? (
            <p className="text-sm text-gray-500">
              The team total appears once {TEAM_TOTAL_MIN_PARTICIPANTS} or more people have joined.
            </p>
          ) : (
            <>
              <p className="text-sm">
                Team total: <strong>{agg.teamTotal}</strong>
                {target !== null ? ` / ${target}` : ''} {unit}
                {agg.teamCompleted && <span className="ml-2 text-green-700">Goal reached</span>}
              </p>
              <div
                className="mt-1 h-3 rounded bg-gray-100"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={target ?? 0}
                aria-valuenow={agg.teamTotal ?? 0}
                data-testid="challenge-team-bar"
              >
                <div
                  className={`h-3 rounded ${agg.teamCompleted ? 'bg-green-600' : 'bg-primary-600'}`}
                  style={{ width: `${pctOf(agg.teamTotal ?? 0, target)}%` }}
                />
              </div>
              {(agg.teamUnknown ?? 0) > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  {agg.teamUnknown} team {agg.teamUnknown === 1 ? 'session has' : 'sessions have'} no measured result yet —
                  not counted, and not held against anyone.
                </p>
              )}
            </>
          )}
        </div>
      )}
      {!cooperative && !agg.withheld && agg.completedCount !== null && (
        <p className="text-sm text-gray-600">
          {agg.completedCount} of {agg.participants} participants have reached the goal.
        </p>
      )}
    </div>
  );
}

export default ChallengeProgressPanel;
